import { runInAction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import React, { useState, useEffect } from 'react';
import {
	getTransactionFees,
	updateTransactionFees,
	TransactionFeesDto,
} from 'Services/Api/_HumanWritten/TransactionService';
import alertToast from 'Util/ToastifyUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { TextArea } from 'Views/Components/New/TextArea';
import { TextInput } from 'Views/Components/New/TextInput';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

interface ChangeFeesFormProps {
	transactionId: string;
	onCancel: () => void;
	onSaveRef: (saveHandler: () => Promise<boolean>) => void;
}

const ChangeFeesForm = observer(({ transactionId, onCancel, onSaveRef }: ChangeFeesFormProps) => {
	const [feesData, setFeesData] = useState<TransactionFeesDto | null>(null);
	const [loading, setLoading] = useState(true);
	const [noteError, setNoteError] = useState<string | undefined>();

	const model = useLocalStore<{
		cancellationFee: number | string;
		giftCertificateValue: number | string;
		note: string;
	}>(() => ({
		cancellationFee: 0,
		giftCertificateValue: 0,
		note: '',
	}));

	useEffect(() => {
		loadFees();
	}, []);

	const loadFees = async () => {
		try {
			setLoading(true);
			const data = await getTransactionFees(transactionId);
			setFeesData(data);
			runInAction(() => {
				model.cancellationFee = data.cancellationFee;
				model.giftCertificateValue = data.giftCertificateValue;
			});
		} catch (error) {
			alertToast('Failed to load transaction fees', 'error');
			onCancel();
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async (): Promise<boolean> => {
		if (!feesData) return false;

		if (!model.note.trim()) {
			setNoteError('A note is required when changing fees');
			return false;
		}

		try {
			await updateTransactionFees(
				transactionId,
				Number(model.cancellationFee),
				Number(model.giftCertificateValue),
				model.note,
			);
			alertToast('Fees updated successfully', 'success');
			return true;
		} catch (error) {
			alertToast('Failed to update fees', 'error');
			return false;
		}
	};

	// Expose handleSave to parent
	useEffect(() => {
		onSaveRef(handleSave);
	}, [feesData, model.cancellationFee, model.giftCertificateValue, model.note]);

	const total = Number(model.cancellationFee) + Number(model.giftCertificateValue);

	if (loading) {
		return (
			<div className="change-fees-form__loading loader-container">
				<div className="loading-text dots">Loading</div>
			</div>
		);
	}

	if (!feesData) {
		return <div>Failed to load transaction fees</div>;
	}

	const maxAmount = feesData.totalCancellationAmount;
	const minVoucherValue = feesData.voucherUsedAmount;

	// Nothing was paid, so there's no payment or voucher: staff edit the fee only, bounded by the
	// booking fare rather than by a refund pool.
	const { isFeeOnly, isAlteration } = feesData;

	// An alteration has no fixed refund pool to re-split: the fee is edited freely up to the fare, and
	// whatever comes off it is credited back to the customer as a voucher.
	const originalFee = feesData.cancellationFee;
	const originalVoucher = feesData.giftCertificateValue;
	const feeAmount = Number(model.cancellationFee) || 0;
	// A paid alteration's fee is bounded by what it would have been with nobody intervening, not by what
	// it is now. That still refuses an increase past what the customer was charged — nothing collects
	// one — while letting an earlier waiver be undone, since the credit that waiver issued absorbs it. A
	// fee nobody has touched has its full amount here, so its ceiling is where it already sits.
	const feeCeiling = isAlteration && !isFeeOnly ? feesData.maximumFee : 0;
	const hasFeeCeiling = feeCeiling > 0;
	const usesFareCap = isFeeOnly || isAlteration;
	const hasFareCap = usesFareCap && feesData.bookingTotalCost > 0;

	// Preview of the voucher the server will write. The server derives this itself and ignores
	// whatever we send, from the fee it actually persisted — so treat this as display only, and expect
	// it to differ if a booking cap trims the fee down.
	const deriveAlterationVoucher = (fee: number) =>
		Math.round(Math.max(minVoucherValue, originalVoucher + (originalFee - fee)) * 100) / 100;

	const onCancellationFeeBlur = () => {
		runInAction(() => {
			// A zero fare means it couldn't be resolved (the fare comes from the ferry booking
			// summaries, so an event booking yields nothing) — cap on nothing rather than on $0,
			// which would silently wipe the fee. The server guards the same way.
			const fareCap = hasFareCap ? feesData.bookingTotalCost : maxAmount - minVoucherValue;
			const capped =
				hasFareCap || !usesFareCap
					? Math.max(0, Math.min(Number(model.cancellationFee) || 0, fareCap))
					: Math.max(0, Number(model.cancellationFee) || 0);
			// The no-intervention ceiling wins over the fare cap where it is the tighter of the two.
			const fee = hasFeeCeiling ? Math.min(capped, feeCeiling) : capped;
			model.cancellationFee = Math.round(fee * 100) / 100;

			if (isFeeOnly) {
				// Invoiced transactions have no voucher, so leave it at zero.
				return;
			}

			model.giftCertificateValue = isAlteration
				? deriveAlterationVoucher(model.cancellationFee)
				: // Paid cancellations split a fixed pool between fee and voucher.
					Math.round((maxAmount - model.cancellationFee) * 100) / 100;
		});
	};

	const onVoucherValueBlur = () => {
		runInAction(() => {
			const voucher = Math.max(minVoucherValue, Math.min(Number(model.giftCertificateValue) || 0, maxAmount));
			model.giftCertificateValue = Math.round(voucher * 100) / 100;
			model.cancellationFee = Math.round((maxAmount - model.giftCertificateValue) * 100) / 100;
		});
	};

	const renderSummaryDetail = () => {
		if (isFeeOnly) {
			// Nothing useful to show when the fare couldn't be resolved — no cap is applied either.
			return hasFareCap ? (
				<div>
					<p className="label">Max fee</p>
					<p className="refund-amount">${feesData.bookingTotalCost.toFixed(2)}</p>
				</div>
			) : null;
		}
		if (isAlteration) {
			// Derived from the live input rather than the model, so it tracks while typing instead of
			// only catching up on blur.
			return (
				<div>
					<p className="label">Credit voucher</p>
					<p className="refund-amount">${deriveAlterationVoucher(feeAmount).toFixed(2)}</p>
				</div>
			);
		}
		if (minVoucherValue > 0) {
			return (
				<div>
					<p className="label">Min/Max voucher</p>
					<p className="refund-amount">
						${minVoucherValue.toFixed(2)}-${maxAmount.toFixed(2)}
					</p>
				</div>
			);
		}
		return (
			<div>
				<p className="label">Max Voucher</p>
				<p className="refund-amount">${maxAmount.toFixed(2)}</p>
			</div>
		);
	};

	return (
		<div className="change-fees-form">
			{!isFeeOnly && !isAlteration && minVoucherValue > 0 && (
				<MessageBox status="warning" slim>
					{
						<p>
							<strong>${minVoucherValue.toFixed(2)}</strong> of the voucher has already been used. The
							voucher value cannot be set below this amount.
						</p>
					}
				</MessageBox>
			)}

			{hasFeeCeiling && (
				<MessageBox status={minVoucherValue > 0 ? 'warning' : 'info'} slim>
					{
						<>
							<p>
								Whatever you take off the fee is credited back to the customer as a voucher. It can be
								set anywhere between <strong>$0.00</strong> and{' '}
								<strong>${feeCeiling.toFixed(2)}</strong>.
							</p>
							{/* Raising the fee is paid for by shrinking that voucher, so credit the customer has
							    already spent cannot fund it — say so, or the ceiling looks arbitrary. */}
							{minVoucherValue > 0 && (
								<p>
									<strong>${minVoucherValue.toFixed(2)}</strong> of the voucher has already been used
									on another booking and can&apos;t be taken back, which is what limits how far the
									fee can go up.
								</p>
							)}
							{minVoucherValue <= 0 && (
								<p>
									The ceiling is what was charged before anyone changed it, so a fee you reduced
									earlier can be put back &mdash; but not above what the customer actually paid.
								</p>
							)}
						</>
					}
				</MessageBox>
			)}

			<div className="change-fees-form__row">
				<TextInput
					label={feesData.feeLabel || (isAlteration ? 'Alteration fee' : 'Cancellation fee')}
					required
					startAdornment="$"
					value={model.cancellationFee ?? ''}
					onChange={e =>
						runInAction(() => {
							model.cancellationFee = e.target.value;
						})
					}
					onBlur={onCancellationFeeBlur}
				/>

				{!isFeeOnly && !isAlteration && (
					<TextInput
						label="Voucher value"
						startAdornment="$"
						value={model.giftCertificateValue ?? ''}
						onChange={e =>
							runInAction(() => {
								model.giftCertificateValue = e.target.value;
							})
						}
						onBlur={onVoucherValueBlur}
					/>
				)}
			</div>

			<TextArea
				label="Note"
				required
				placeholder="Reason for changing fees..."
				value={model.note ?? ''}
				error={noteError}
				onChange={e => {
					runInAction(() => {
						model.note = e.target.value;
					});
					setNoteError(undefined);
				}}
			/>

			{feesData.customerCanViewNotes && (
				<div className="change-fees-form__note-visibility">
					<Icon name="help" useMaterialIcons classname="change-fees-form__note-visibility-icon" />
					<span>
						This note will be <strong>visible to the customer</strong>.
					</span>
				</div>
			)}

			<div className="form-summary">
				<div>
					<p className="label">{isFeeOnly || isAlteration ? 'Fee' : 'Total'}</p>
					<p className="price">${(isFeeOnly || isAlteration ? feeAmount : total).toFixed(2)}</p>
				</div>
				{renderSummaryDetail()}
			</div>
		</div>
	);
});

export function showChangeFeesModal(transactionId: string, onSuccess?: () => void) {
	let handleSaveRef: (() => Promise<boolean>) | null = null;

	return newConfirmModal(
		'Change Transaction Fees',
		<ChangeFeesForm
			transactionId={transactionId}
			onCancel={() => {}}
			onSaveRef={saveHandler => {
				handleSaveRef = saveHandler;
			}}
		/>,
		{
			confirmText: 'Save Changes',
			cancelText: 'Cancel',
			shouldCloseOnOverlayClick: false,
			shouldCloseOnEsc: true,
			showCloseButton: true,
			onBeforeConfirm: async () => {
				if (handleSaveRef) {
					const result = await handleSaveRef();
					if (result) {
						onSuccess?.();
					}
					return result;
				}
				return false;
			},
		},
	);
}
